import React, { useEffect, useState } from 'react';
import {
  LinkLegacy as Link,
  useParamsLegacy as useParams,
} from '@leagueplatform/routing';
import {
  HeadingOne,
  BodyOne,
  Box,
  Flex,
} from '@leagueplatform/genesis-commons';
import { useIntl } from '@leagueplatform/locales';
import {
  CONTENT_MAX_WIDTH,
  noopFunction,
} from '#constants/challenges.constants.tsx';
import {
  BackButtonArrow,
  FullPageError,
} from '@leagueplatform/web-common-components';
import { JOURNEY_ROUTES } from '@leagueplatform/health-journey-common';
import { track } from '@leagueplatform/analytics';
import { CHALLENGE_CAMPAIGN_TYPE } from '#constants/analytics.constants.tsx';
import { useGetChallengeByIdData } from '#pages/challenge-landing-page/hooks/use-get-challenge-by-id-data.hook.ts';
import { isEnrollmentOpen } from '#utils/enrollment-period-status.util.ts';
import { handleStaticAsset } from '@leagueplatform/asset-config';
import { useGetUserProfile } from '#hooks/use-get-user-profile.hook.ts';
import { CHALLENGES_ASSET_KEYS } from '#types/challenges.types.ts';
import { TeamsOption } from './components/teams-option/teams-option.component';
import { JoinTeam } from './components/join-team/join-team.component';
import { JoinTeamPageSkeletonLoader } from './components/join-team-page-skeleton-loader/join-team-page-skeleton-loader.component';
import { TEAM_CHOICE } from './constants/join-team-page.constants';
import { CreateTeam } from './components/create-team/create-team.component';

export const JoinTeamPage = (): JSX.Element => {
  const [teamChoice, setTeamChoice] = useState<TEAM_CHOICE | undefined>();
  const { formatMessage } = useIntl();
  const { challengeId } = useParams<{ challengeId: string }>();
  const { isLoading, error, data: challengeData } = useGetChallengeByIdData();
  const { data: userData } = useGetUserProfile();
  const { default_timezone: defaultTimezone } = userData || {};
  useEffect(() => {
    if (!teamChoice && challengeData?.data) {
      track('challenges.screen_loaded.challenge_join_or_create_a_team', {
        health_campaign_id: challengeId || '',
        health_campaign_name: challengeData.data.title || '',
        health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
        challenge_status: challengeData.data.status || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeData, teamChoice]);

  if (isLoading) return <JoinTeamPageSkeletonLoader />;

  if (error || !challengeData?.data)
    return (
      <FullPageError error={error} route={JOURNEY_ROUTES.programsHome()} />
    );

  const { endEnroll, maxTeamSize, startEnroll } = challengeData.data;
  const enrollmentOpen = isEnrollmentOpen(
    startEnroll,
    endEnroll,
    defaultTimezone,
  );
  const userIsEnrolled =
    challengeData.data?.userChallenge?.data?.enrollmentStatus === 'enrolled';
  const teamsEnabled = maxTeamSize > 0;
  const userIsOnTeam = !!challengeData.data.challengeTeam?.data.id;
  // TODO: move userCanJoinTeam logic to select method on query (https://everlong.atlassian.net/browse/CHAL-1854)
  const userCanJoinTeam =
    userIsEnrolled && teamsEnabled && !userIsOnTeam && enrollmentOpen;

  // Display error if someone tries to access the /join-team route directly (not via a valid state from CLP) when they shouldn't be able to
  if (!userCanJoinTeam)
    return <FullPageError error={null} route={JOURNEY_ROUTES.programsHome()} />;

  if (teamChoice === TEAM_CHOICE.JOIN)
    return <JoinTeam backTo={() => setTeamChoice(undefined)} />;

  if (teamChoice === TEAM_CHOICE.CREATE)
    return <CreateTeam backTo={() => setTeamChoice(undefined)} />;

  const joinTeam = handleStaticAsset(
    CHALLENGES_ASSET_KEYS.CHALLENGES_JOIN_TEAM,
  ) as string;
  const createTeam = handleStaticAsset(
    CHALLENGES_ASSET_KEYS.CHALLENGES_CREATE_TEAM,
  ) as string;

  return (
    <Box
      marginX={{ _: 'twoAndHalf', tablet: 'auto' }}
      marginY="five"
      maxWidth={CONTENT_MAX_WIDTH}
    >
      <Flex alignItems="center">
        <BackButtonArrow
          as={Link}
          marginInlineEnd={{ _: 'one', phone: 'twoAndHalf' }}
          marginTop="half"
          onClick={noopFunction}
          to={JOURNEY_ROUTES.getChallengeDetails(challengeId)}
        />
        <HeadingOne>{formatMessage({ id: 'TEAMS' })}</HeadingOne>
      </Flex>
      <Box marginInlineStart="four">
        <BodyOne marginTop="one" marginBottom="three">
          {formatMessage({ id: 'CHALLENGE_TEAMS_DESCRIPTION' })}
        </BodyOne>
        <Flex flexDirection="column" gap="one">
          <TeamsOption
            image={joinTeam}
            heading={formatMessage({ id: 'JOIN_A_TEAM' })}
            description={formatMessage({ id: 'JOIN_TEAM_DESCRIPTION' })}
            setChoice={() => setTeamChoice(TEAM_CHOICE.JOIN)}
          />
          <TeamsOption
            image={createTeam}
            heading={formatMessage({ id: 'CREATE_A_TEAM' })}
            description={formatMessage({ id: 'CREATE_TEAM_DESCRIPTION' })}
            setChoice={() => setTeamChoice(TEAM_CHOICE.CREATE)}
          />
        </Flex>
      </Box>
    </Box>
  );
};
